import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../../../services/setup";
import { Toast } from "../../../components/errorNotifier";
import { loader } from "../../../components/LoadingSpinner";

const STUDENTS_PER_PAGE = 50;

const formatMoney = (val) => {
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: 2,
  }).format(Number(val || 0));
};

const StudentsFinancesReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState("");

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionFilter, setSessionFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [programmeFilter, setProgrammeFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Pagination & Drill-down
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [expandedStatement, setExpandedStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      loader({ title: "Loading Finance Reports", text: "Please wait..." });
      const resp = await axios.get(`${baseUrl}finance/students_finance_report`);
      const data = resp.data?.data || [];
      setStudents(data);
      Toast.fire({ icon: "success", title: "Report loaded successfully" });
    } catch (err) {
      console.error("Failed to load students finance report:", err);
      const message =
        err?.response?.data?.message || err.message || "Failed to load report";
      setError(message);
      Toast.fire({ icon: "error", title: message });
    } finally {
      setLoading(false);
    }
  };

  // Toggle and fetch statement for student drill-down
  const toggleStatement = async (email) => {
    if (expandedEmail === email) {
      setExpandedEmail(null);
      setExpandedStatement(null);
      return;
    }

    setExpandedEmail(email);
    setExpandedStatement(null);
    setStatementLoading(true);

    try {
      const resp = await axios.get(
        `${baseUrl}finance/student_financial_statement`,
        { params: { email } }
      );
      if (resp.data?.status === "success") {
        setExpandedStatement(resp.data.data);
      } else {
        Toast.fire({
          icon: "warning",
          title: resp.data?.message || "Statement unavailable",
        });
      }
    } catch (err) {
      console.error("Error loading statement for " + email, err);
      Toast.fire({ icon: "error", title: "Failed to load student statement" });
    } finally {
      setStatementLoading(false);
    }
  };

  // Extract distinct filter options from current student list
  const filterLists = useMemo(() => {
    const departments = Array.from(
      new Set(students.map((s) => s.Department).filter(Boolean))
    ).sort();
    const programmes = Array.from(
      new Set(
        students
          .filter((s) => !departmentFilter || s.Department === departmentFilter)
          .map((s) => s.Programme)
          .filter(Boolean)
      )
    ).sort();
    const sessions = Array.from(
      new Set(students.map((s) => s.SessionOfEntry).filter(Boolean))
    ).sort((a, b) => b.localeCompare(a));
    const levels = Array.from(
      new Set(students.map((s) => s.Level).filter(Boolean))
    ).sort();

    return { departments, programmes, sessions, levels };
  }, [students, departmentFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchTerm,
    sessionFilter,
    departmentFilter,
    programmeFilter,
    levelFilter,
    statusFilter,
  ]);

  // Filtered dataset
  const filteredStudents = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();

    return students.filter((s) => {
      if (sessionFilter && s.SessionOfEntry !== sessionFilter) return false;
      if (departmentFilter && s.Department !== departmentFilter) return false;
      if (programmeFilter && s.Programme !== programmeFilter) return false;
      if (levelFilter && s.Level !== levelFilter) return false;

      // Financial status filter
      if (statusFilter !== "all") {
        const netDebt = Number(s.NetOutstanding || 0);
        const unpaid = Number(s.TotalUnpaidInvoiceAmount || 0);
        const wallet = Number(s.AccountBalance || 0);

        if (statusFilter === "debtor" && netDebt <= 0) return false;
        if (statusFilter === "cleared" && unpaid > 0) return false;
        if (statusFilter === "covered" && (unpaid === 0 || wallet < unpaid))
          return false;
        if (statusFilter === "surplus" && Number(s.NetSurplus || 0) <= 0)
          return false;
      }

      // Search term
      if (q) {
        const searchPool = [
          s.MatricNumber,
          s.ApplicationNo,
          s.Email,
          s.PhoneNumber,
          s.Fullname,
          s.Department,
          s.Programme,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        if (!searchPool.includes(q)) return false;
      }

      return true;
    });
  }, [
    students,
    searchTerm,
    sessionFilter,
    departmentFilter,
    programmeFilter,
    levelFilter,
    statusFilter,
  ]);

  // Financial Stats & KPI calculations
  const stats = useMemo(() => {
    let totalBilled = 0;
    let totalPaid = 0;
    let totalWaiver = 0;
    let totalUnpaid = 0;
    let totalOutstanding = 0;
    let totalWallet = 0;
    let debtorsCount = 0;
    let clearedCount = 0;
    let coveredCount = 0;

    filteredStudents.forEach((s) => {
      const billed = Number(s.TotalBilled || 0);
      const paid = Number(s.TotalPaid || 0);
      const waiver = Number(s.TotalWaiver || s.Waiver || 0);
      const unpaid = Number(
        s.TotalUnpaidInvoiceAmount || s.TotalInvoiceAmount || 0
      );
      const debt = Number(s.NetOutstanding || 0);
      const bal = Number(s.AccountBalance || 0);

      totalBilled += billed;
      totalPaid += paid;
      totalWaiver += waiver;
      totalUnpaid += unpaid;
      totalOutstanding += debt;
      totalWallet += bal;

      if (debt > 0) debtorsCount++;
      else if (unpaid === 0) clearedCount++;
      else if (bal >= unpaid) coveredCount++;
    });

    return {
      totalStudents: filteredStudents.length,
      totalBilled,
      totalPaid,
      totalWaiver,
      totalUnpaid,
      totalOutstanding,
      totalWallet,
      debtorsCount,
      clearedCount,
      coveredCount,
    };
  }, [filteredStudents]);

  // Pagination calculation
  const totalPages = Math.max(
    1,
    Math.ceil(filteredStudents.length / STUDENTS_PER_PAGE)
  );
  const startIndex = (currentPage - 1) * STUDENTS_PER_PAGE;
  const pageStudents = filteredStudents.slice(
    startIndex,
    startIndex + STUDENTS_PER_PAGE
  );

  // Client-side CSV export
  const handleExportCSV = () => {
    if (!filteredStudents.length) {
      Toast.fire({ icon: "info", title: "No data to export" });
      return;
    }

    const headers = [
      "Application No",
      "Matric Number",
      "Full Name",
      "Email",
      "Phone Number",
      "Department",
      "Programme",
      "Level",
      "Session Of Entry",
      "Total Invoiced (Billed)",
      "Total Paid",
      "Waiver",
      "Unpaid Invoices",
      "Wallet Balance",
      "Net Outstanding Debt",
      "Net Surplus",
      "Financial Status",
    ];

    const rows = filteredStudents.map((s) => [
      `"${s.ApplicationNo || ""}"`,
      `"${s.MatricNumber || ""}"`,
      `"${(s.Fullname || "").replace(/"/g, '""')}"`,
      `"${s.Email || ""}"`,
      `"${s.PhoneNumber || ""}"`,
      `"${(s.Department || "").replace(/"/g, '""')}"`,
      `"${(s.Programme || "").replace(/"/g, '""')}"`,
      `"${s.Level || ""}"`,
      `"${s.SessionOfEntry || ""}"`,
      Number(s.TotalBilled || 0).toFixed(2),
      Number(s.TotalPaid || 0).toFixed(2),
      Number(s.TotalWaiver || s.Waiver || 0).toFixed(2),
      Number(s.TotalUnpaidInvoiceAmount || s.TotalInvoiceAmount || 0).toFixed(2),
      Number(s.AccountBalance || 0).toFixed(2),
      Number(s.NetOutstanding || 0).toFixed(2),
      Number(s.NetSurplus || 0).toFixed(2),
      `"${s.FinancialStatus || (Number(s.NetOutstanding || 0) > 0 ? "Debtor" : "Cleared")}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `students_finance_report_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    Toast.fire({ icon: "success", title: "CSV downloaded successfully" });
  };

  const getStatusBadge = (s) => {
    const netDebt = Number(s.NetOutstanding || 0);
    const unpaid = Number(
      s.TotalUnpaidInvoiceAmount || s.TotalInvoiceAmount || 0
    );
    const wallet = Number(s.AccountBalance || 0);

    if (netDebt > 0) {
      return (
        <span className="badge bg-danger rounded-pill px-2 py-1">
          <i className="fas fa-exclamation-triangle me-1"></i>Debtor
        </span>
      );
    }
    if (unpaid > 0 && wallet >= unpaid) {
      return (
        <span className="badge bg-warning text-dark rounded-pill px-2 py-1">
          <i className="fas fa-wallet me-1"></i>Covered by Wallet
        </span>
      );
    }
    if (unpaid === 0 && wallet > 0) {
      return (
        <span className="badge bg-info text-dark rounded-pill px-2 py-1">
          <i className="fas fa-plus-circle me-1"></i>Credit Surplus
        </span>
      );
    }
    return (
      <span className="badge bg-success rounded-pill px-2 py-1">
        <i className="fas fa-check-circle me-1"></i>Fully Cleared
      </span>
    );
  };

  return (
    <div className="container-fluid py-4 px-md-4">
      {/* Header bar */}
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center mb-4 gap-3">
        <div>
          <h3 className="fw-bold mb-1 text-dark">
            <i className="fas fa-file-invoice-dollar text-primary me-2"></i>
            Students Finance Report
          </h3>
          <p className="text-muted small mb-0">
            Comprehensive audit of student fee billings, collections, wallet
            balances, and net outstanding liabilities.
          </p>
        </div>

        <div className="d-flex flex-wrap gap-2">
          <button
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={fetchReport}
            disabled={loading}
          >
            <i className={`fas fa-sync-alt ${loading ? "fa-spin" : ""}`}></i>
            Refresh
          </button>

          <button
            className="btn btn-success d-flex align-items-center gap-2"
            onClick={handleExportCSV}
            disabled={!filteredStudents.length}
          >
            <i className="fas fa-file-csv"></i>
            Export CSV
          </button>

          <button
            className="btn btn-outline-dark d-flex align-items-center gap-2 d-none d-md-flex"
            onClick={() => window.print()}
          >
            <i className="fas fa-print"></i>
            Print
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger shadow-sm">{error}</div>}

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        {/* Total Students */}
        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card border-0 shadow-sm h-100 bg-white">
            <div className="card-body">
              <span className="text-muted small fw-semibold text-uppercase">
                Total Students
              </span>
              <h4 className="fw-bold mt-2 mb-1 text-dark">
                {stats.totalStudents}
              </h4>
              <div className="small text-muted">
                <span className="text-danger fw-semibold">
                  {stats.debtorsCount}
                </span>{" "}
                debtors &bull;{" "}
                <span className="text-success fw-semibold">
                  {stats.clearedCount}
                </span>{" "}
                cleared
              </div>
            </div>
          </div>
        </div>

        {/* Total Billed */}
        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card border-0 shadow-sm h-100 bg-white">
            <div className="card-body">
              <span className="text-muted small fw-semibold text-uppercase">
                Gross Invoiced
              </span>
              <h4 className="fw-bold mt-2 mb-1 text-primary">
                {formatMoney(stats.totalBilled)}
              </h4>
              <small className="text-muted">Total fees billed</small>
            </div>
          </div>
        </div>

        {/* Total Revenue Collected */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card border-0 shadow-sm h-100 bg-white">
            <div className="card-body">
              <span className="text-muted small fw-semibold text-uppercase">
                Total Revenue Collected
              </span>
              <h4 className="fw-bold mt-2 mb-1 text-success">
                {formatMoney(stats.totalPaid)}
              </h4>
              <small className="text-muted">
                Verified invoice collections
              </small>
            </div>
          </div>
        </div>

        {/* Total Outstanding Debt */}
        <div className="col-12 col-sm-6 col-xl-3">
          <div className="card border-0 shadow-sm h-100 bg-white border-start border-4 border-danger">
            <div className="card-body">
              <span className="text-danger small fw-semibold text-uppercase">
                Total Net Outstanding Debt
              </span>
              <h4 className="fw-bold mt-2 mb-1 text-danger">
                {formatMoney(stats.totalOutstanding)}
              </h4>
              <small className="text-muted">
                Unpaid invoices exceeding wallet funds
              </small>
            </div>
          </div>
        </div>

        {/* Available Wallet Balances */}
        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card border-0 shadow-sm h-100 bg-white">
            <div className="card-body">
              <span className="text-muted small fw-semibold text-uppercase">
                Wallet Funds
              </span>
              <h4 className="fw-bold mt-2 mb-1 text-info">
                {formatMoney(stats.totalWallet)}
              </h4>
              <small className="text-muted">Retained student credits</small>
            </div>
          </div>
        </div>
      </div>

      {/* Multi-Filter Bar */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-2 align-items-center">
            {/* Search Input */}
            <div className="col-12 col-lg-3">
              <div className="input-group">
                <span className="input-group-text bg-light border-end-0">
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="search"
                  className="form-control border-start-0"
                  placeholder="Search matric, name, app no, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearchTerm("")}
                    title="Clear search"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </div>

            {/* Session Filter */}
            <div className="col-6 col-md-3 col-lg-2">
              <select
                className="form-select"
                value={sessionFilter}
                onChange={(e) => setSessionFilter(e.target.value)}
              >
                <option value="">All Sessions</option>
                {filterLists.sessions.map((sess) => (
                  <option key={sess} value={sess}>
                    {sess}
                  </option>
                ))}
              </select>
            </div>

            {/* Department Filter */}
            <div className="col-6 col-md-3 col-lg-2">
              <select
                className="form-select"
                value={departmentFilter}
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setProgrammeFilter(""); // reset programme on department change
                }}
              >
                <option value="">All Departments</option>
                {filterLists.departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
              </select>
            </div>

            {/* Programme Filter */}
            <div className="col-6 col-md-3 col-lg-2">
              <select
                className="form-select"
                value={programmeFilter}
                onChange={(e) => setProgrammeFilter(e.target.value)}
              >
                <option value="">All Programmes</option>
                {filterLists.programmes.map((prog) => (
                  <option key={prog} value={prog}>
                    {prog}
                  </option>
                ))}
              </select>
            </div>

            {/* Level Filter */}
            <div className="col-6 col-md-3 col-lg-1">
              <select
                className="form-select"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <option value="">All Levels</option>
                {filterLists.levels.map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl}
                  </option>
                ))}
              </select>
            </div>

            {/* Financial Status Filter */}
            <div className="col-12 col-md-6 col-lg-2">
              <select
                className="form-select fw-semibold"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Financial Statuses</option>
                <option value="debtor">Debtors (Debt &gt; 0)</option>
                <option value="covered">Covered by Wallet</option>
                <option value="cleared">Fully Cleared (₦0)</option>
                <option value="surplus">Credit Surplus</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Student Records Table */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white py-3 d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
          <div className="fw-semibold text-dark">
            Student Financial Records
            <span className="badge bg-light text-dark ms-2 border">
              {filteredStudents.length} matching students
            </span>
          </div>

          {totalPages > 1 && (
            <div className="small text-muted">
              Showing {startIndex + 1} &ndash;{" "}
              {Math.min(startIndex + STUDENTS_PER_PAGE, filteredStudents.length)}{" "}
              of {filteredStudents.length}
            </div>
          )}
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr className="small text-muted text-uppercase">
                  <th style={{ minWidth: 220 }}>Student Details</th>
                  <th>Academic Info</th>
                  <th>Total Billed</th>
                  <th>Total Paid</th>
                  <th>Waiver</th>
                  <th>Wallet Balance</th>
                  <th>Net Outstanding</th>
                  <th>Status</th>
                  <th className="text-center" style={{ width: 120 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageStudents.map((s) => {
                  const isExpanded = expandedEmail === s.Email;
                  const netDebt = Number(s.NetOutstanding || 0);

                  return (
                    <React.Fragment
                      key={s.Email || s.ApplicationNo || s.MatricNumber}
                    >
                      <tr className={isExpanded ? "table-active" : ""}>
                        {/* Student Details */}
                        <td>
                          <div className="fw-bold text-dark">{s.Fullname}</div>
                          <div className="small text-muted font-monospace">
                            {s.MatricNumber || s.ApplicationNo}
                          </div>
                          <div className="small text-muted">
                            <i className="fas fa-envelope me-1"></i>
                            {s.Email}
                          </div>
                        </td>

                        {/* Academic Info */}
                        <td>
                          <div className="small fw-semibold">
                            {s.Department || "—"}
                          </div>
                          <div className="small text-muted">
                            {s.Programme || "—"}
                          </div>
                          <div className="small text-muted">
                            <span className="badge bg-light text-secondary border me-1">
                              {s.Level || "—"}
                            </span>
                            {s.SessionOfEntry}
                          </div>
                        </td>

                        {/* Total Billed */}
                        <td className="fw-semibold text-dark">
                          {formatMoney(s.TotalBilled)}
                        </td>

                        {/* Total Paid */}
                        <td className="fw-semibold text-success">
                          {formatMoney(s.TotalPaid)}
                        </td>

                        {/* Waiver */}
                        <td className="fw-semibold text-muted">
                          {Number(s.TotalWaiver || s.Waiver || 0) > 0 ? (
                            <span className="text-primary">
                              {formatMoney(s.TotalWaiver || s.Waiver)}
                            </span>
                          ) : (
                            formatMoney(0)
                          )}
                        </td>

                        {/* Wallet Balance */}
                        <td className="fw-semibold text-info">
                          {formatMoney(s.AccountBalance)}
                        </td>

                        {/* Net Outstanding */}
                        <td>
                          <span
                            className={`fw-bold ${
                              netDebt > 0 ? "text-danger" : "text-success"
                            }`}
                          >
                            {formatMoney(netDebt)}
                          </span>
                        </td>

                        {/* Financial Status Badge */}
                        <td>{getStatusBadge(s)}</td>

                        {/* Actions */}
                        <td className="text-center">
                          <button
                            className={`btn btn-sm ${
                              isExpanded
                                ? "btn-secondary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => toggleStatement(s.Email)}
                            title="View student invoices and statement"
                          >
                            <i
                              className={`fas ${
                                isExpanded
                                  ? "fa-chevron-up"
                                  : "fa-receipt"
                              } me-1`}
                            ></i>
                            {isExpanded ? "Close" : "Statement"}
                          </button>
                        </td>
                      </tr>

                      {/* Expanded Financial Statement Drawer */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="9" className="p-0 border-0">
                            <div className="bg-light p-4 border-top border-bottom">
                              {/* Quick Actions Header */}
                              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                <div>
                                  <h6 className="fw-bold mb-0 text-dark">
                                    <i className="fas fa-file-invoice text-primary me-2"></i>
                                    Financial Statement &bull; {s.Fullname}
                                  </h6>
                                  <small className="text-muted">
                                    Phone: {s.PhoneNumber || "—"} &bull; Email:{" "}
                                    {s.Email} &bull; Matric:{" "}
                                    {s.MatricNumber || s.ApplicationNo}
                                  </small>
                                </div>

                                <div className="d-flex flex-wrap gap-2 mt-2 mt-md-0">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() =>
                                      navigate(
                                        `/admin/account-reconciliation?email=${encodeURIComponent(
                                          s.Email
                                        )}`
                                      )
                                    }
                                  >
                                    <i className="fas fa-balance-scale me-1"></i>
                                    Reconcile Account
                                  </button>

                                  <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() =>
                                      navigate(
                                        `/admin/create-invoice?email=${encodeURIComponent(
                                          s.Email
                                        )}`
                                      )
                                    }
                                  >
                                    <i className="fas fa-plus me-1"></i>
                                    Create Invoice
                                  </button>

                                  <button
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() =>
                                      navigate(
                                        `/admin/manual-payment?email=${encodeURIComponent(
                                          s.Email
                                        )}`
                                      )
                                    }
                                  >
                                    <i className="fas fa-hand-holding-usd me-1"></i>
                                    Manual Payment
                                  </button>
                                </div>
                              </div>

                              {statementLoading && (
                                <div className="text-center py-4">
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="visually-hidden">
                                      Loading statement...
                                    </span>
                                  </div>
                                  <p className="mt-2 text-muted small">
                                    Fetching invoices and ledger history...
                                  </p>
                                </div>
                              )}

                              {!statementLoading && expandedStatement && (
                                <div className="row g-4">
                                  {/* Invoices List */}
                                  <div className="col-12 col-xl-7">
                                    <div className="card shadow-sm border-0">
                                      <div className="card-header bg-white py-2">
                                        <span className="fw-semibold small text-uppercase">
                                          Issued Invoices (
                                          {expandedStatement.invoices?.length ||
                                            0}
                                          )
                                        </span>
                                      </div>
                                      <div className="table-responsive">
                                        <table className="table table-sm table-striped mb-0 small">
                                          <thead>
                                            <tr>
                                              <th>Code</th>
                                              <th>Title</th>
                                              <th>Billed</th>
                                              <th>Paid</th>
                                              <th>Waiver</th>
                                              <th>Due</th>
                                              <th>Status</th>
                                              <th>Date</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(
                                              expandedStatement.invoices || []
                                            ).map((inv) => (
                                              <tr key={inv.id || inv.invoice_code}>
                                                <td className="font-monospace">
                                                  {inv.invoice_code}
                                                </td>
                                                <td>{inv.title}</td>
                                                <td>
                                                  {formatMoney(inv.net_amount)}
                                                </td>
                                                <td className="text-success">
                                                  {formatMoney(inv.amount_paid)}
                                                </td>
                                                <td className="text-muted">
                                                  {formatMoney(inv.waiver)}
                                                </td>
                                                <td className="text-danger fw-semibold">
                                                  {formatMoney(inv.amount_due)}
                                                </td>
                                                <td>
                                                  <span
                                                    className={`badge ${
                                                      inv.calculated_status ===
                                                      "Paid"
                                                        ? "bg-success"
                                                        : inv.calculated_status ===
                                                          "Partial"
                                                        ? "bg-warning text-dark"
                                                        : "bg-danger"
                                                    }`}
                                                  >
                                                    {inv.calculated_status}
                                                  </span>
                                                </td>
                                                <td>{inv.invoice_date || "—"}</td>
                                              </tr>
                                            ))}
                                            {!expandedStatement.invoices
                                              ?.length && (
                                              <tr>
                                                <td
                                                  colSpan="8"
                                                  className="text-center py-3 text-muted"
                                                >
                                                  No invoices found for this
                                                  student.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Recent Transactions / Wallet Ledger */}
                                  <div className="col-12 col-xl-5">
                                    <div className="card shadow-sm border-0">
                                      <div className="card-header bg-white py-2">
                                        <span className="fw-semibold small text-uppercase">
                                          Recent Transactions / Ledger
                                        </span>
                                      </div>
                                      <div className="table-responsive">
                                        <table className="table table-sm table-striped mb-0 small">
                                          <thead>
                                            <tr>
                                              <th>Ref</th>
                                              <th>Details</th>
                                              <th>Amount</th>
                                              <th>Date</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(
                                              expandedStatement.transactions || []
                                            ).map((tx, idx) => (
                                              <tr
                                                key={
                                                  tx.TransactionID ||
                                                  tx.id ||
                                                  idx
                                                }
                                              >
                                                <td className="font-monospace text-truncate" style={{ maxWidth: 100 }}>
                                                  {tx.TransactionID || tx.reference}
                                                </td>
                                                <td className="text-truncate" style={{ maxWidth: 140 }}>
                                                  {tx.Details || tx.description || "Payment"}
                                                </td>
                                                <td className="fw-semibold text-success">
                                                  {formatMoney(tx.Amount || tx.amount)}
                                                </td>
                                                <td>{tx.Date || tx.entry_date || "—"}</td>
                                              </tr>
                                            ))}
                                            {!expandedStatement.transactions
                                              ?.length && (
                                              <tr>
                                                <td
                                                  colSpan="4"
                                                  className="text-center py-3 text-muted"
                                                >
                                                  No recent transactions
                                                  recorded.
                                                </td>
                                              </tr>
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {!pageStudents.length && (
                  <tr>
                    <td colSpan="9" className="text-center py-5 text-muted">
                      {loading ? (
                        <div>
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 mb-0">Loading student records...</p>
                        </div>
                      ) : (
                        <div>
                          <i className="fas fa-folder-open fa-3x text-muted mb-3 d-block"></i>
                          <h5>No students match your filter criteria</h5>
                          <p className="text-muted small mb-0">
                            Try adjusting your search query, session, department,
                            or financial status filter.
                          </p>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="card-footer bg-white py-3 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-2">
            <span className="small text-muted">
              Page {currentPage} of {totalPages}
            </span>

            <ul className="pagination pagination-sm mb-0">
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
              </li>
              <li
                className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>

              {/* Page Number Windows */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let p = currentPage - 2 + i;
                if (p < 1) p = i + 1;
                if (p > totalPages) return null;
                return (
                  <li
                    key={p}
                    className={`page-item ${p === currentPage ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                    </button>
                  </li>
                );
              })}

              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
              <li
                className={`page-item ${
                  currentPage === totalPages ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentsFinancesReport;
